import React from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { CheckCircle, Clock, AlertCircle, Circle } from 'lucide-react';
import { DemoBanner } from '../../components/DemoBanner';
import { relocationChecklist } from '../../mockData';

const RelocationChecklist = () => {
  const completedTasks = relocationChecklist.filter(item => item.status === 'completed').length;
  const totalTasks = relocationChecklist.length;
  const progressPercentage = Math.round((completedTasks / totalTasks) * 100);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-6 w-6 text-green-600" />;
      case 'in-progress':
        return <Clock className="h-6 w-6 text-yellow-600" />;
      case 'pending':
        return <AlertCircle className="h-6 w-6 text-orange-600" />;
      default:
        return <Circle className="h-6 w-6 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'completed': 'bg-green-100 text-green-800',
      'in-progress': 'bg-yellow-100 text-yellow-800',
      'pending': 'bg-orange-100 text-orange-800',
      'not-started': 'bg-gray-100 text-gray-800'
    };
    return variants[status] || variants['not-started'];
  };

  const categories = [...new Set(relocationChecklist.map(item => item.category))];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Relocation Checklist</h1>
            <p className="text-gray-600">Track your progress through the relocation process</p>
          </div>

          {/* Demo Banner */}
          <DemoBanner 
            type="preview"
            title="Preview Feature"
            description="This checklist shows sample relocation steps. Your actual progress will be tracked once you begin your relocation journey with an employer."
          />

          {/* Progress Overview */}
          <Card className="mb-8">
            <CardContent className="p-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">Overall Progress</h2>
                  <p className="text-gray-600">{completedTasks} of {totalTasks} tasks completed</p>
                </div>
                <div className="text-4xl font-bold text-[#42b1c2]">{progressPercentage}%</div>
              </div>
              <Progress value={progressPercentage} className="h-3" />
            </CardContent>
          </Card>

          {/* Checklist by Category */}
          {categories.map((category) => {
            const categoryTasks = relocationChecklist.filter(item => item.category === category);
            const categoryCompleted = categoryTasks.filter(item => item.status === 'completed').length;
            
            return (
              <Card key={category} className="mb-6">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl">{category}</CardTitle>
                    <span className="text-sm text-gray-600">
                      {categoryCompleted} / {categoryTasks.length} completed
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {categoryTasks.map((item) => (
                      <div key={item.id} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex-shrink-0 mt-1">
                          {getStatusIcon(item.status)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <h3 className="font-semibold text-gray-900">{item.task}</h3>
                            <Badge className={getStatusBadge(item.status)}>
                              {item.status.replace('-', ' ')}
                            </Badge>
                          </div>
                          {item.status === 'completed' && (
                            <p className="text-sm text-green-600">✓ Completed</p>
                          )}
                          {item.status === 'in-progress' && (
                            <p className="text-sm text-yellow-600">In progress - check back soon</p>
                          )}
                          {item.status === 'pending' && (
                            <p className="text-sm text-orange-600">Waiting for document submission</p>
                          )}
                          {item.status === 'not-started' && (
                            <p className="text-sm text-gray-600">Not yet started</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* Help Card */}
          <Card className="bg-gradient-to-br from-[#42b1c2]/10 to-[#edd982]/10">
            <CardContent className="p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Need Help?</h3>
              <p className="text-gray-700 mb-4">
                Our relocation support team is here to assist you at every step. If you have questions about any of these tasks, please don't hesitate to reach out.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="mailto:support@lumamedglobal.com" className="text-[#42b1c2] hover:text-[#3a9fb0] font-medium">
                  support@lumamedglobal.com
                </a>
                <span className="text-gray-400">|</span>
                <span className="text-gray-700">+966 XX XXX XXXX</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RelocationChecklist;